import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  BarChart3, TrendingUp, Calendar, DollarSign, Users, FileText, 
  Loader2, ArrowUpRight, ArrowDownRight, PieChart, Clock, Download, FileSpreadsheet
} from 'lucide-react';
import { contractsAPI, eventsAPI, invoicesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { escapeHtml } from '../utils/escapeHtml';
import DOMPurify from 'dompurify';
import { parseLocalDate, formatLocalDate } from '../utils/dateUtils';

const Analytics = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [events, setEvents] = useState([]);
  const [timeRange, setTimeRange] = useState('year');
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomRange, setShowCustomRange] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractsData, eventsData] = await Promise.all([
        contractsAPI.getAll(),
        eventsAPI.getAll()
      ]);
      setContracts(contractsData);
      setEvents(eventsData);

      try {
        const stats = await invoicesAPI.getStats();
        setInvoiceStats(stats);
      } catch (e) {
        console.warn('Invoice stats unavailable:', e);
      }
    } catch (error) {
      toast({ title: 'Error loading analytics', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Calculate date ranges
  const today = new Date();
  const getDateRange = () => {
    const end = new Date();
    let start = new Date();
    
    if (timeRange === 'custom' && customDateRange.start && customDateRange.end) {
      return { start: new Date(customDateRange.start), end: new Date(customDateRange.end) };
    }
    
    switch (timeRange) {
      case 'week': start.setDate(end.getDate() - 7); break;
      case 'month': start.setMonth(end.getMonth() - 1); break;
      case 'quarter': start.setMonth(end.getMonth() - 3); break;
      case 'year': start.setFullYear(end.getFullYear() - 1); break;
      default: start.setFullYear(end.getFullYear() - 1);
    }
    return { start, end };
  };

  const { start: rangeStart, end: rangeEnd } = getDateRange();

  // Filter contracts by date range
  const filteredContracts = useMemo(() => contracts.filter(c => {
    const date = parseLocalDate(c.event_date);
    return date && date >= rangeStart && date <= rangeEnd;
  }), [contracts, rangeStart, rangeEnd]);

  // Upcoming events memoized list
  const upcomingEventsList = useMemo(() =>
    contracts
      .filter(c => {
        const d = parseLocalDate(c.event_date);
        return d && d >= today;
      })
      .sort((a, b) => parseLocalDate(a.event_date) - parseLocalDate(b.event_date))
      .slice(0, 5),
    [contracts, today]
  );

  // Calculate metrics
  const totalRevenue = filteredContracts.reduce((sum, c) => sum + (c.total_amount || 0), 0);
  const totalGuests = filteredContracts.reduce((sum, c) => sum + (c.guest_count || 0), 0);
  const avgContractValue = filteredContracts.length > 0 ? totalRevenue / filteredContracts.length : 0;
  const avgGuestsPerEvent = filteredContracts.length > 0 ? totalGuests / filteredContracts.length : 0;

  // Previous period for comparison
  const prevRangeEnd = new Date(rangeStart);
  const prevRangeStart = new Date(rangeStart);
  switch (timeRange) {
    case 'week': prevRangeStart.setDate(prevRangeStart.getDate() - 7); break;
    case 'month': prevRangeStart.setMonth(prevRangeStart.getMonth() - 1); break;
    case 'quarter': prevRangeStart.setMonth(prevRangeStart.getMonth() - 3); break;
    case 'year': prevRangeStart.setFullYear(prevRangeStart.getFullYear() - 1); break;
  }

  const prevContracts = contracts.filter(c => {
    const date = parseLocalDate(c.event_date);
    return date && date >= prevRangeStart && date < prevRangeEnd;
  });

  const prevRevenue = prevContracts.reduce((sum, c) => sum + (c.total_amount || 0), 0);
  const revenueChange = prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue * 100) : 0;

  // Event type breakdown
  const eventTypeBreakdown = filteredContracts.reduce((acc, c) => {
    const type = c.event_type || c.location_type || 'Other';
    if (!acc[type]) acc[type] = { count: 0, revenue: 0 };
    acc[type].count++;
    acc[type].revenue += c.total_amount || 0;
    return acc;
  }, {});

  // Monthly revenue data
  const monthlyData = [];
  for (let i = 11; i >= 0; i--) {
    const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() - i + 1, 0);
    const monthContracts = contracts.filter(c => {
      const date = parseLocalDate(c.event_date);
      return date && date >= monthDate && date <= monthEnd;
    });
    monthlyData.push({
      month: monthDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      revenue: monthContracts.reduce((sum, c) => sum + (c.total_amount || 0), 0),
      events: monthContracts.length
    });
  }

  // Status breakdown
  const statusBreakdown = filteredContracts.reduce((acc, c) => {
    const status = c.status || 'pending';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const maxMonthlyRevenue = Math.max(...monthlyData.map(m => m.revenue), 1);

  // ========== REVENUE PROJECTIONS ==========
  // Future months projection data
  const futureMonthsData = [];
  const upcomingContracts = contracts.filter(c => {
    const d = parseLocalDate(c.event_date);
    return d && d >= today;
  });
  
  // Group upcoming contracts by month
  const upcomingByMonth = {};
  upcomingContracts.forEach(c => {
    const date = parseLocalDate(c.event_date);
    if (!date) return;
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!upcomingByMonth[monthKey]) {
      upcomingByMonth[monthKey] = { confirmed: 0, pending: 0, total: 0 };
    }
    if (c.status === 'confirmed') {
      upcomingByMonth[monthKey].confirmed += c.total_amount || 0;
    } else {
      upcomingByMonth[monthKey].pending += c.total_amount || 0;
    }
    upcomingByMonth[monthKey].total += c.total_amount || 0;
  });

  // Calculate average monthly revenue for trend projection
  const avgMonthlyRevenue = monthlyData.reduce((sum, m) => sum + m.revenue, 0) / 12;
  const recentAvg = monthlyData.slice(-3).reduce((sum, m) => sum + m.revenue, 0) / 3;

  // Generate next 6 months projection
  for (let i = 1; i <= 6; i++) {
    const futureDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const monthKey = `${futureDate.getFullYear()}-${String(futureDate.getMonth() + 1).padStart(2, '0')}`;
    const bookedData = upcomingByMonth[monthKey] || { confirmed: 0, pending: 0, total: 0 };
    
    // Projected revenue = booked + estimated (based on historical trend)
    // For months without bookings, use trend line
    const trendProjection = recentAvg * (1 + (i * 0.02)); // 2% growth per month trend
    
    futureMonthsData.push({
      month: futureDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      confirmed: bookedData.confirmed,
      pending: bookedData.pending,
      projected: Math.max(bookedData.total, trendProjection * 0.7), // Use higher of booked or 70% of trend
      trend: trendProjection
    });
  }

  // Projection totals
  const totalConfirmedPipeline = upcomingContracts
    .filter(c => c.status === 'confirmed')
    .reduce((sum, c) => sum + (c.total_amount || 0), 0);
  const totalPendingPipeline = upcomingContracts
    .filter(c => c.status !== 'confirmed')
    .reduce((sum, c) => sum + (c.total_amount || 0), 0);
  const totalPipeline = totalConfirmedPipeline + totalPendingPipeline;

  // Payment status breakdown
  const paymentStatusBreakdown = filteredContracts.reduce((acc, c) => {
    const status = c.payment_status || 'pending';
    acc[status] = (acc[status] || 0) + (c.total_amount || 0);
    return acc;
  }, {});

  const totalCollected = paymentStatusBreakdown.paid || 0;
  const totalDeposits = paymentStatusBreakdown.deposit_paid || 0;
  const totalOutstanding = (paymentStatusBreakdown.pending || 0) + (paymentStatusBreakdown.partial || 0);

  const maxProjectedRevenue = Math.max(...futureMonthsData.map(m => m.trend), 1);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Client Name', 'Event Name', 'Event Date', 'Event Type', 'Guest Count', 'Total Amount', 'Status'];
    const rows = filteredContracts.map(c => [
      c.client_name || '',
      c.event_name || '',
      c.event_date || '',
      c.event_type || c.location_type || '',
      c.guest_count || 0,
      c.total_amount || 0,
      c.status || 'pending'
    ]);

    // Add summary rows
    rows.push([]);
    rows.push(['SUMMARY']);
    rows.push(['Total Revenue', '', '', '', '', totalRevenue, '']);
    rows.push(['Total Events', '', '', '', '', filteredContracts.length, '']);
    rows.push(['Total Guests', '', '', '', '', totalGuests, '']);
    rows.push(['Avg Contract Value', '', '', '', '', avgContractValue.toFixed(2), '']);
    rows.push([]);
    rows.push(['MONTHLY BREAKDOWN']);
    rows.push(['Month', 'Revenue', 'Events']);
    monthlyData.forEach(m => {
      rows.push([m.month, m.revenue, m.events]);
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `analytics_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    toast({ title: 'CSV exported successfully!' });
  };

  // Export to PDF
  const exportToPDF = async () => {
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      
      // Create a styled report for PDF
      const reportContent = document.createElement('div');
      reportContent.innerHTML = DOMPurify.sanitize(`
        <div style="font-family: Arial, sans-serif; padding: 40px; max-width: 800px;">
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1e3a5f; padding-bottom: 20px;">
            <h1 style="color: #1e3a5f; margin: 0; font-size: 28px;">Business Analytics Report</h1>
            <p style="color: #666; margin-top: 10px;">
              ${rangeStart.toLocaleDateString()} - ${rangeEnd.toLocaleDateString()}
            </p>
          </div>

          <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 30px;">
            <div style="background: #f0fdf4; padding: 20px; border-radius: 10px; border-left: 4px solid #22c55e;">
              <p style="margin: 0; color: #666; font-size: 14px;">Total Revenue</p>
              <p style="margin: 5px 0 0; font-size: 28px; font-weight: bold; color: #1e3a5f;">$${totalRevenue.toLocaleString()}</p>
            </div>
            <div style="background: #eff6ff; padding: 20px; border-radius: 10px; border-left: 4px solid #3b82f6;">
              <p style="margin: 0; color: #666; font-size: 14px;">Total Events</p>
              <p style="margin: 5px 0 0; font-size: 28px; font-weight: bold; color: #1e3a5f;">${filteredContracts.length}</p>
            </div>
            <div style="background: #faf5ff; padding: 20px; border-radius: 10px; border-left: 4px solid #a855f7;">
              <p style="margin: 0; color: #666; font-size: 14px;">Total Guests</p>
              <p style="margin: 5px 0 0; font-size: 28px; font-weight: bold; color: #1e3a5f;">${totalGuests.toLocaleString()}</p>
            </div>
            <div style="background: #fffbeb; padding: 20px; border-radius: 10px; border-left: 4px solid #f59e0b;">
              <p style="margin: 0; color: #666; font-size: 14px;">Avg Contract Value</p>
              <p style="margin: 5px 0 0; font-size: 28px; font-weight: bold; color: #1e3a5f;">$${avgContractValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
            </div>
          </div>

          <div style="margin-bottom: 30px;">
            <h2 style="color: #1e3a5f; border-bottom: 2px solid #eee; padding-bottom: 10px;">Event Type Breakdown</h2>
            <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
              <thead>
                <tr style="background: #f5f5dc;">
                  <th style="padding: 12px; text-align: left; border-bottom: 2px solid #1e3a5f;">Event Type</th>
                  <th style="padding: 12px; text-align: center; border-bottom: 2px solid #1e3a5f;">Events</th>
                  <th style="padding: 12px; text-align: right; border-bottom: 2px solid #1e3a5f;">Revenue</th>
                </tr>
              </thead>
              <tbody>
                ${Object.entries(eventTypeBreakdown).map(([type, data]) => `
                  <tr>
                    <td style="padding: 12px; border-bottom: 1px solid #eee; text-transform: capitalize;">${escapeHtml(type.replace('_', ' '))}</td>
                    <td style="padding: 12px; text-align: center; border-bottom: 1px solid #eee;">${data.count}</td>
                    <td style="padding: 12px; text-align: right; border-bottom: 1px solid #eee;">$${data.revenue.toLocaleString()}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <div style="margin-bottom: 30px;">
            <h2 style="color: #1e3a5f; border-bottom: 2px solid #eee; padding-bottom: 10px;">Monthly Revenue</h2>
            <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
              <thead>
                <tr style="background: #f5f5dc;">
                  <th style="padding: 10px; text-align: left; border-bottom: 2px solid #1e3a5f;">Month</th>
                  <th style="padding: 10px; text-align: center; border-bottom: 2px solid #1e3a5f;">Events</th>
                  <th style="padding: 10px; text-align: right; border-bottom: 2px solid #1e3a5f;">Revenue</th>
                </tr>
              </thead>
              <tbody>
                ${monthlyData.map(m => `
                  <tr>
                    <td style="padding: 10px; border-bottom: 1px solid #eee;">${m.month}</td>
                    <td style="padding: 10px; text-align: center; border-bottom: 1px solid #eee;">${m.events}</td>
                    <td style="padding: 10px; text-align: right; border-bottom: 1px solid #eee;">$${m.revenue.toLocaleString()}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <div style="margin-bottom: 30px;">
            <h2 style="color: #1e3a5f; border-bottom: 2px solid #eee; padding-bottom: 10px;">Contract Status</h2>
            <div style="display: flex; gap: 20px; margin-top: 15px;">
              ${[
                { status: 'confirmed', label: 'Confirmed', color: '#22c55e' },
                { status: 'pending', label: 'Pending', color: '#eab308' },
                { status: 'draft', label: 'Draft', color: '#9ca3af' },
                { status: 'completed', label: 'Completed', color: '#3b82f6' },
              ].map(({ status, label, color }) => `
                <div style="flex: 1; padding: 15px; background: #f9f9f9; border-radius: 8px; text-align: center; border-left: 4px solid ${color};">
                  <p style="font-size: 24px; font-weight: bold; margin: 0; color: #1e3a5f;">${statusBreakdown[status] || 0}</p>
                  <p style="font-size: 12px; color: #666; margin: 5px 0 0;">${label}</p>
                </div>
              `).join('')}
            </div>
          </div>

          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; text-align: center; color: #999; font-size: 12px;">
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Willow Ridge Catering Management</p>
          </div>
        </div>
      `);

      const opt = {
        margin: 10,
        filename: `analytics_report_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().set(opt).from(reportContent).save();
      toast({ title: 'PDF exported successfully!' });
    } catch (error) {
      console.error('PDF export error:', error);
      toast({ title: 'Error exporting PDF', variant: 'destructive' });
    }
  };

  const handleCustomDateApply = () => {
    if (customDateRange.start && customDateRange.end) {
      setTimeRange('custom');
      setShowCustomRange(false);
      toast({ title: 'Custom date range applied' });
    } else {
      toast({ title: 'Please select both start and end dates', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen flex justify-center items-center">
        <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="analytics-page" ref={reportRef}>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Business Analytics</h1>
          <p className="text-gray-600">Track performance and insights</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export Buttons */}
          <Button
            variant="outline"
            onClick={exportToCSV}
            className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f]/10"
            data-testid="export-csv-btn"
          >
            <FileSpreadsheet size={18} className="mr-2" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={exportToPDF}
            className="border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f]/10"
            data-testid="export-pdf-btn"
          >
            <Download size={18} className="mr-2" />
            Export PDF
          </Button>

          {/* Time Range Select */}
          <Select value={timeRange} onValueChange={(val) => {
            if (val === 'custom') {
              setShowCustomRange(true);
            } else {
              setTimeRange(val);
            }
          }}>
            <SelectTrigger className="w-40" data-testid="time-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last Month</SelectItem>
              <SelectItem value="quarter">Last Quarter</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Custom Date Range Dialog */}
      <Dialog open={showCustomRange} onOpenChange={setShowCustomRange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select Custom Date Range</DialogTitle>
            <DialogDescription>
              Choose a start and end date to filter your analytics data.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                data-testid="custom-start-date"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                data-testid="custom-end-date"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowCustomRange(false)}>
              Cancel
            </Button>
            <Button onClick={handleCustomDateApply} className="bg-[#1e3a5f] hover:bg-[#2d5016]">
              Apply Range
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Display current date range */}
      {timeRange === 'custom' && (
        <div className="mb-4 p-3 bg-[#1e3a5f]/10 rounded-lg inline-flex items-center gap-2">
          <Calendar size={16} className="text-[#1e3a5f]" />
          <span className="text-sm text-[#1e3a5f] font-medium">
            {new Date(customDateRange.start).toLocaleDateString()} - {new Date(customDateRange.end).toLocaleDateString()}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCustomRange(true)}
            className="text-[#1e3a5f] hover:bg-[#1e3a5f]/20 h-6 px-2"
          >
            Edit
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-none shadow-lg">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-[#1e3a5f]" data-testid="total-revenue">${totalRevenue.toLocaleString()}</p>
                <div className={`flex items-center mt-2 text-sm ${revenueChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {revenueChange >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                  <span>{Math.abs(revenueChange).toFixed(1)}% vs prev period</span>
                </div>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="text-green-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Events</p>
                <p className="text-3xl font-bold text-[#1e3a5f]" data-testid="total-events">{filteredContracts.length}</p>
                <p className="text-sm text-gray-500 mt-2">
                  {statusBreakdown.confirmed || 0} confirmed
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="text-blue-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500 mb-1">Total Guests</p>
                <p className="text-3xl font-bold text-[#1e3a5f]" data-testid="total-guests">{totalGuests.toLocaleString()}</p>
                <p className="text-sm text-gray-500 mt-2">
                  Avg {Math.round(avgGuestsPerEvent)} per event
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Users className="text-purple-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500 mb-1">Avg Contract Value</p>
                <p className="text-3xl font-bold text-[#1e3a5f]" data-testid="avg-contract">${avgContractValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                <p className="text-sm text-gray-500 mt-2">
                  ${(avgContractValue / (avgGuestsPerEvent || 1)).toFixed(2)}/guest
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <TrendingUp className="text-amber-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Monthly Revenue Chart */}
        <Card className="lg:col-span-2 border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <BarChart3 size={20} />
              Monthly Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between h-48 gap-2">
              {monthlyData.map((month, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center group">
                  <div className="w-full bg-gray-100 rounded-t relative" style={{ height: '150px' }}>
                    <div
                      className="absolute bottom-0 w-full bg-gradient-to-t from-[#1e3a5f] to-[#2d5016] rounded-t transition-all group-hover:from-[#2d5016] group-hover:to-[#3d6826]"
                      style={{ height: `${(month.revenue / maxMonthlyRevenue) * 100}%` }}
                    />
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#1e3a5f] text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                      ${month.revenue.toLocaleString()}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 mt-2">{month.month}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Event Type Breakdown */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <PieChart size={20} />
              Event Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(eventTypeBreakdown).map(([type, data]) => {
                const percentage = (data.count / filteredContracts.length * 100) || 0;
                return (
                  <div key={type}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{type.replace('_', ' ')}</span>
                      <span className="text-gray-500">{data.count} events</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-[#1e3a5f]"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">${data.revenue.toLocaleString()} revenue</p>
                  </div>
                );
              })}
              {Object.keys(eventTypeBreakdown).length === 0 && (
                <p className="text-gray-500 text-center py-4">No event data</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Projections Section */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
          <TrendingUp size={24} />
          Revenue Projections
        </h2>
        
        {/* Pipeline Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-none shadow-lg bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="p-4">
              <p className="text-sm text-green-700 mb-1">Confirmed Pipeline</p>
              <p className="text-2xl font-bold text-green-800">${totalConfirmedPipeline.toLocaleString()}</p>
              <p className="text-xs text-green-600 mt-1">
                {upcomingContracts.filter(c => c.status === 'confirmed').length} events
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-lg bg-gradient-to-br from-yellow-50 to-yellow-100">
            <CardContent className="p-4">
              <p className="text-sm text-yellow-700 mb-1">Pending Pipeline</p>
              <p className="text-2xl font-bold text-yellow-800">${totalPendingPipeline.toLocaleString()}</p>
              <p className="text-xs text-yellow-600 mt-1">
                {upcomingContracts.filter(c => c.status !== 'confirmed').length} events
              </p>
            </CardContent>
          </Card>
          
          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <p className="text-sm text-blue-700 mb-1">Total Pipeline</p>
              <p className="text-2xl font-bold text-blue-800">${totalPipeline.toLocaleString()}</p>
              <p className="text-xs text-blue-600 mt-1">
                {upcomingContracts.length} upcoming events
              </p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4">
              <p className="text-sm text-purple-700 mb-1">Avg Monthly Trend</p>
              <p className="text-2xl font-bold text-purple-800">${recentAvg.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
              <p className="text-xs text-purple-600 mt-1">Based on last 3 months</p>
            </CardContent>
          </Card>
        </div>

        {/* Projection Chart */}
        <Card className="border-none shadow-lg mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <BarChart3 size={20} />
              6-Month Revenue Forecast
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between h-56 gap-3">
              {futureMonthsData.map((month, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center group">
                  <div className="w-full bg-gray-100 rounded-t relative" style={{ height: '180px' }}>
                    {/* Trend projection (background) */}
                    <div
                      className="absolute bottom-0 w-full bg-gray-300 rounded-t opacity-40"
                      style={{ height: `${(month.trend / maxProjectedRevenue) * 100}%` }}
                    />
                    {/* Confirmed revenue (solid) */}
                    <div
                      className="absolute bottom-0 w-full bg-gradient-to-t from-green-600 to-green-400 rounded-t"
                      style={{ height: `${(month.confirmed / maxProjectedRevenue) * 100}%` }}
                    />
                    {/* Pending revenue (striped) */}
                    <div
                      className="absolute bottom-0 w-full bg-gradient-to-t from-yellow-500 to-yellow-300 rounded-t opacity-70"
                      style={{ 
                        height: `${((month.confirmed + month.pending) / maxProjectedRevenue) * 100}%`,
                        background: month.pending > 0 ? 'repeating-linear-gradient(45deg, #eab308, #eab308 5px, #fde047 5px, #fde047 10px)' : 'transparent'
                      }}
                    />
                    {/* Tooltip */}
                    <div className="absolute -top-16 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#1e3a5f] text-white text-xs px-3 py-2 rounded whitespace-nowrap z-10">
                      <p className="font-bold">{month.month}</p>
                      <p>Confirmed: ${month.confirmed.toLocaleString()}</p>
                      <p>Pending: ${month.pending.toLocaleString()}</p>
                      <p className="text-gray-300">Trend: ${month.trend.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                    </div>
                  </div>
                  <span className="text-sm font-medium text-gray-700 mt-2">{month.month}</span>
                  <span className="text-xs text-gray-500">${(month.confirmed + month.pending).toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-6 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gradient-to-t from-green-600 to-green-400 rounded" />
                <span className="text-sm text-gray-600">Confirmed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ background: 'repeating-linear-gradient(45deg, #eab308, #eab308 2px, #fde047 2px, #fde047 4px)' }} />
                <span className="text-sm text-gray-600">Pending</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-300 rounded opacity-40" />
                <span className="text-sm text-gray-600">Trend Projection</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Collection Status */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <DollarSign size={20} />
              Payment Collection Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-green-700">Fully Paid</span>
                  <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                    {filteredContracts.filter(c => c.payment_status === 'paid').length} contracts
                  </span>
                </div>
                <p className="text-2xl font-bold text-green-800">${totalCollected.toLocaleString()}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-blue-700">Deposits Received</span>
                  <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                    {filteredContracts.filter(c => c.payment_status === 'deposit_paid').length} contracts
                  </span>
                </div>
                <p className="text-2xl font-bold text-blue-800">${totalDeposits.toLocaleString()}</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-amber-700">Outstanding</span>
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                    {filteredContracts.filter(c => !c.payment_status || c.payment_status === 'pending' || c.payment_status === 'partial').length} contracts
                  </span>
                </div>
                <p className="text-2xl font-bold text-amber-800">${totalOutstanding.toLocaleString()}</p>
              </div>
            </div>
            
            {/* Collection Progress Bar */}
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Collection Progress</span>
                <span className="text-[#1e3a5f] font-medium">
                  ${(totalCollected + totalDeposits).toLocaleString()} of ${totalRevenue.toLocaleString()} ({totalRevenue > 0 ? ((totalCollected + totalDeposits) / totalRevenue * 100).toFixed(1) : 0}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div className="h-full flex">
                  <div 
                    className="bg-green-500 h-full"
                    style={{ width: totalRevenue > 0 ? `${(totalCollected / totalRevenue * 100)}%` : '0%' }}
                  />
                  <div 
                    className="bg-blue-500 h-full"
                    style={{ width: totalRevenue > 0 ? `${(totalDeposits / totalRevenue * 100)}%` : '0%' }}
                  />
                </div>
              </div>
              <div className="flex justify-center gap-6 mt-2">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded" />
                  <span className="text-xs text-gray-600">Fully Paid</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded" />
                  <span className="text-xs text-gray-600">Deposits</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-gray-200 rounded" />
                  <span className="text-xs text-gray-600">Outstanding</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contract Status */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <FileText size={20} />
              Contract Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              {[
                { status: 'confirmed', label: 'Confirmed', color: 'bg-green-500' },
                { status: 'pending', label: 'Pending', color: 'bg-yellow-500' },
                { status: 'draft', label: 'Draft', color: 'bg-gray-400' },
                { status: 'completed', label: 'Completed', color: 'bg-blue-500' },
              ].map(({ status, label, color }) => (
                <div key={status} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-3 h-3 rounded-full ${color}`} />
                  <div>
                    <p className="text-2xl font-bold text-[#1e3a5f]">{statusBreakdown[status] || 0}</p>
                    <p className="text-xs text-gray-500">{label}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="border-none shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <Clock size={20} />
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingEventsList.map((contract) => (
                  <div key={contract.id || `${contract.event_date}-${contract.client_name}`} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-[#1e3a5f]">{contract.client_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatLocalDate(contract.event_date, { month: 'numeric', day: 'numeric', year: 'numeric' })} • {contract.guest_count} guests
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      contract.status === 'confirmed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {contract.status || 'pending'}
                    </span>
                  </div>
                ))}
              {upcomingEventsList.length === 0 && (
                <p className="text-gray-500 text-center py-4">No upcoming events</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
